import { useQuery } from '@apollo/client';
import { createContext, useEffect, useMemo, useState } from 'react';

import colors from 'lib/colors';

import { meWithTutorialQuery } from 'graphql/queries';
import { MeWithTutorialQuery_me } from 'graphql/types';
import {
  MeWithTutorialQuery,
  MeWithTutorialQuery_me_attendees,
  CurrentScenes,
  CurrentScenesVariables,
  SceneStatusEnum,
  GameDifficulty,
  ScenesProgress,
  ScenesProgress_playableScenes,
  ScenesProgress_playableScenes_progress,
  ScenesProgressVariables,
  TrainingType,
} from 'graphql/types';

import useNotificationService from 'pages/Game/utils/NotificationService';

import { useLoader } from 'components/LoadingProvider/LoadingProvider';
import { compareWithPriority } from 'utils/sortAndCompare';
// import useLayoutRedirect from 'utils/useLayoutRedirect';

import { scenesProgressQuery, currentScenesQuery } from './operations';

type Progress = Omit<ScenesProgress_playableScenes_progress, '__typename'>;

export interface SceneWithProgress
  extends Omit<ScenesProgress_playableScenes, 'progress'> {
  progress: Progress | null;
  statuses?: SceneStatusEnum[] | null;
}

export type TrainingCompletionRequirements = any;

interface ScenesProgressContextType {
  currentScenes: SceneWithProgress[];
  attendees: MeWithTutorialQuery_me_attendees[];
  isActiveTraining?: boolean;
  freeGameScenes: SceneWithProgress[];
  trainingScenes: SceneWithProgress[];
  tournamentScenes: SceneWithProgress[];
  allScenesCompleted: boolean;
  tournamentActive: boolean;
  mediumGameInTraining: boolean;
  trainingRequirements?: TrainingCompletionRequirements;
  firstTimeLoading: boolean;
}

export const ScenesProgressContext = createContext<ScenesProgressContextType>({
  currentScenes: [],
  attendees: [],
  isActiveTraining: false,
  freeGameScenes: [],
  trainingScenes: [],
  tournamentScenes: [],
  allScenesCompleted: false,
  tournamentActive: false,
  mediumGameInTraining: false,
  trainingRequirements: undefined,
  firstTimeLoading: false,
});

interface Props {
  children: React.ReactNode | null;
  user?: MeWithTutorialQuery_me;
  isEduLayout?: boolean;
}

const CLOSE_LOADING_OVERLAY_TIMEOUT = 2000; // [ms]

export const ScenesProgressProvider = ({
  children,
  user,
  isEduLayout,
}: Props) => {
  const { loading: loadingUserData, data: MeData } =
    useQuery<MeWithTutorialQuery>(meWithTutorialQuery, {
      fetchPolicy: 'cache-first',
      variables: {
        activeTrainingsOnly: isEduLayout, // hide archived trainings on edu layout
      },
    });

  // NOTE it is not necessary yet
  // const { redirectToLayout } = useLayoutRedirect();
  // redirectToLayout({
  //   companyLayout: MeData?.me?.company?.layout.code || '',
  //   isEdu: !!isEduLayout,
  // });

  const [isLoading, setIsLoading] = useState(false);

  const { loading: loadingCurrentSceneProgress, data: currentSceneProgress } =
    useQuery<CurrentScenes, CurrentScenesVariables>(currentScenesQuery, {
      fetchPolicy: 'cache-and-network',
      variables: {
        gameDifficulty:
          MeData?.me?.gameDifficulty ||
          user?.gameDifficulty ||
          GameDifficulty.EASY,
        progressInput: {},
      },
      skip: !isEduLayout,
    });

  const { loading: loadingTrainingSceneProgress, data: trainingSceneProgress } =
    useQuery<ScenesProgress, ScenesProgressVariables>(scenesProgressQuery, {
      fetchPolicy: 'cache-and-network',
      variables: {
        input: { trainingType: TrainingType.TRAINING },
        progressInput: { trainingType: TrainingType.TRAINING },
      },
      skip: !!isEduLayout,
    });

  const {
    loading: loadingTournamentSceneProgress,
    data: tournamentSceneProgress,
  } = useQuery<ScenesProgress, ScenesProgressVariables>(scenesProgressQuery, {
    fetchPolicy: 'cache-and-network',
    variables: {
      input: { trainingType: TrainingType.TOURNAMENT },
      progressInput: { trainingType: TrainingType.TOURNAMENT },
    },
    skip: !!isEduLayout,
  });

  const withDelay =
    parseInt(localStorage.getItem('sse.animationDelay') || '', 10) ||
    CLOSE_LOADING_OVERLAY_TIMEOUT;
  useLoader(isLoading, { withDelay, color: colors.blueBackground + 'A0' });

  const { requestNotification } = useNotificationService();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(requestNotification, []);

  // CLS-80/79
  useEffect(() => {
    if (
      loadingUserData ||
      loadingTrainingSceneProgress ||
      loadingTournamentSceneProgress ||
      loadingCurrentSceneProgress
    ) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [
    loadingTrainingSceneProgress,
    loadingTournamentSceneProgress,
    loadingUserData,
    loadingCurrentSceneProgress,
  ]);

  const partialContext = useMemo(() => {
    const currentScenes: SceneWithProgress[] = [];
    currentSceneProgress?.currentScenes.forEach((scene) => {
      // NOTE Added training status here, because it is not possible to do it on backend. The progress is created in different (training) service
      const nextScene = {
        ...scene,
        statuses: scene?.progress?.training
          ? [...(scene.statuses || []), SceneStatusEnum.TRAINING]
          : scene.statuses,
      };
      currentScenes.push({ ...nextScene });
    });
    const freeGameScenes: SceneWithProgress[] = [];
    const trainingScenes: SceneWithProgress[] = [];
    const trainingRequirements =
      MeData?.me?.company?.trainingCompletionRequirements;
    let allScenesCompleted = false;
    let allTournamentsCompleted = false;
    if (trainingSceneProgress) {
      trainingSceneProgress.playableScenes.forEach((scene) => {
        if (scene.progress) {
          const sceneProgress = createSceneWithProgress(scene, MeData);

          trainingScenes.push(sceneProgress);
        }
        freeGameScenes.push({ ...scene, progress: null });
      });
      allScenesCompleted = trainingScenes.every(
        (tr) => tr.progress?.alreadyFinished,
      );
    }
    const tournamentScenes =
      tournamentSceneProgress?.playableScenes
        .filter((scene) => scene.progress && scene.progress.tournament)
        .map((scene) => {
          return createSceneWithProgress(scene, MeData);
        }) || [];
    const tournamentActive = !!tournamentScenes.length;

    return {
      // EDU layout
      currentScenes: currentScenes.sort(compareWithPriority),
      attendees: MeData?.me?.attendees || [],
      isActiveTraining: MeData?.me?.company?.isActiveTraining,
      // Old layout
      freeGameScenes,
      trainingScenes,
      tournamentScenes,
      trainingRequirements,
      tournamentActive,
      allScenesCompleted,
      allTournamentsCompleted,
      mediumGameInTraining:
        trainingRequirements?.winAgainstCPU ||
        trainingRequirements?.finishAgainstPlayer ||
        false,
    };
  }, [
    MeData,
    trainingSceneProgress,
    tournamentSceneProgress,
    currentSceneProgress,
  ]);

  return (
    <ScenesProgressContext.Provider
      value={{
        ...partialContext,
        firstTimeLoading: loadingUserData || loadingTrainingSceneProgress,
      }}
    >
      {children}
    </ScenesProgressContext.Provider>
  );
};

function createSceneWithProgress(
  scene: ScenesProgress_playableScenes,
  user?: MeWithTutorialQuery,
) {
  const trainingRequirements =
    user?.me?.company?.trainingCompletionRequirements;
  return {
    ...scene,
    progress: getSceneProgress(scene.progress, trainingRequirements),
  };
}

function getSceneProgress(
  progress: Progress | null,
  requirements?: TrainingCompletionRequirements,
): Progress {
  if (!progress || !requirements) {
    return {
      gameCount: 0,
      alreadyFinished: false,
      tournament: false,
    };
  }
  return progress;
}
